// src/components/AdvancedStatsHelpModal.jsx
import React from 'react';

const ADVANCED_HELP = {
  BLOC1: {
    title: 'Répartition des points gagnés',
    color: '#1976D2',
    items: [
      { name: 'Points gagnants', desc: "Coups qui terminent directement le point sans que l'adversaire ne touche la balle : ace, coup gagnant, service gagnant. Chaque type de coup (service, coup droit, revers, montée au filet) est représenté séparément." },
      { name: 'Fautes adverses', desc: "Points gagnés grâce à une erreur de l'adversaire : faute directe au filet, en couloir ou en longueur. Indique dans quelle mesure on bénéficie des erreurs adverses plutôt que de gagner sur ses propres mérites." },
      { name: 'Lecture du graphique', desc: "Le donut montre la proportion de chaque source de points gagnés. Un joueur qui gagne beaucoup sur fautes adverses est moins dominant qu'un joueur qui gagne sur ses propres coups gagnants." },
    ]
  },
  BLOC2: {
    title: 'Comparaison de chaque coup',
    color: '#FF9800',
    items: [
      { name: 'Barres doubles', desc: "Pour chaque type de coup (service, coup droit, revers, filet), deux barres sont affichées : points gagnants (haut) et fautes directes (bas). Permet de visualiser le rapport gagnants/fautes par coup." },
      { name: 'Ratio gagnants/fautes', desc: "Un bon ratio est >1 : plus de points gagnants que de fautes. Un ratio <1 indique que le coup génère plus d'erreurs que de victoires directes." },
      { name: 'Lecture comparative', desc: "La comparaison côte à côte des deux joueurs révèle les coups dominants de chacun et les coups faibles à exploiter tactiquement." },
    ]
  },
  BLOC3: {
    title: 'Coups décisifs',
    color: '#388E3C',
    items: [
      { name: 'Coups expliquant la victoire', desc: "Coups sur lesquels le joueur analysé a un avantage net sur l'adversaire : soit plus de points gagnants, soit moins de fautes que l'adversaire sur ce même coup. Ces coups ont contribué positivement au résultat." },
      { name: 'Coups expliquant la défaite', desc: "Coups sur lesquels l'adversaire avait un avantage net. Ces coups ont pesé négativement sur le résultat du match." },
      { name: 'Lecture du graphique', desc: "Plus la part d'un coup est grande dans le donut, plus il a été déterminant dans l'explication du résultat. Idéal pour cibler les priorités de travail à l'entraînement." },
    ]
  },
  BLOC4: {
    title: 'Statistiques par zones',
    color: '#7B1FA2',
    items: [
      { name: 'Zones de service', desc: "Le court est divisé en zones numérotées (T, corps, extérieur, côté égalité/avantage). Chaque barre montre le nombre d'aces/services gagnants (haut) et de doubles fautes (bas) par zone." },
      { name: 'Zones de retour', desc: "Montre les points gagnés et les fautes commises en retour selon la zone d'atterrissage du service adverse. Permet d'identifier les zones où le retour est le plus efficace ou le plus fragile." },
      { name: 'Utilité tactique', desc: "Identifier les zones de service les plus productives permet d'orienter la tactique en match : servir davantage dans les zones à fort taux de réussite et éviter les zones à risque." },
    ]
  },
  BLOC5: {
    title: 'Statistiques par direction',
    color: '#C62828',
    items: [
      { name: 'Directions du coup droit', desc: "Pour chaque direction possible du coup droit (croisé, long de ligne, inside-out, inside-in, inside-middle), nombre de points gagnants et de fautes directes. Identifie les directions préférées et les moins maîtrisées." },
      { name: 'Directions du revers', desc: "Même analyse pour le revers. Un revers croisé est généralement plus sûr (filet plus bas, plus de marge) qu'un long de ligne." },
      { name: 'Points et fautes à la volée par direction', desc: "Analyse des montées au filet selon la direction visée (croisé, long de ligne, centre). Montre les directions les plus efficaces au filet et celles qui génèrent le plus de fautes. Un bon volleyeur doit maîtriser toutes les directions pour ne pas être lu par l'adversaire." },
      { name: 'Utilité tactique', desc: "Connaître les directions à éviter (trop de fautes) et celles à privilégier (beaucoup de gagnants) permet d'affiner la tactique de jeu en match." },
    ]
  },
  BLOC6: {
    title: 'Longueur de rallye',
    color: '#0277BD',
    items: [
      { name: 'Catégories de rallye', desc: "Les points sont regroupés par longueur d'échange : service/retour (1-2 coups), court (3-4 coups), moyen (5-8 coups), long (9+ coups). Chaque catégorie montre les points gagnés et perdus." },
      { name: 'Profil de jeu', desc: "Un joueur dominant dans les rallyes courts préfère le jeu rapide (service-volée, coup gagnant direct). Un joueur fort dans les longs échanges est un joueur de fond de court endurant." },
      { name: 'Point de rupture', desc: "Identifier à partir de quelle longueur de rallye le joueur commence à perdre plus qu'il ne gagne permet de définir sa longueur de rallye optimale." },
    ]
  },
};

function AdvancedStatsHelpModal({ section, onClose }) {
  const help = ADVANCED_HELP[section];
  if (!help) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '16px',
          padding: '20px', maxWidth: '380px', width: '100%',
          maxHeight: '80vh', overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: help.color }}>
            {help.title}
          </h3>
          <button onClick={onClose} style={{
            border: 'none', background: 'none', fontSize: '20px',
            cursor: 'pointer', color: '#999', lineHeight: 1,
          }}>✕</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {help.items.map((item, i) => (
            <div key={i} style={{
              padding: '10px 12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              borderLeft: `3px solid ${help.color}`,
            }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: help.color, marginBottom: '4px' }}>
                {item.name}
              </div>
              <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.5' }}>
                {item.desc}
              </div>
            </div>
          ))}
        </div>

        <button onClick={onClose} style={{
          marginTop: '16px', width: '100%', padding: '11px',
          backgroundColor: help.color, color: 'white', border: 'none',
          borderRadius: '8px', fontSize: '14px', fontWeight: '600',
          cursor: 'pointer',
        }}>
          Fermer
        </button>
      </div>
    </div>
  );
}

export default AdvancedStatsHelpModal;